export type Urgency = 'routine' | 'urgent' | 'emergent';

export type AuthorizationStatus = 
  | 'pending_surgeon_approval'
  | 'surgeon_approved'
  | 'packet_generated'
  | 'submitted_to_payer'
  | 'payer_pending'
  | 'more_info_requested'
  | 'payer_approved'
  | 'payer_denied'
  | 'appeal_in_progress'
  | 'awaiting_post_op_reconciliation'
  | 'resolved'
  | 'closed';

export type PayerResponseType = 'approved' | 'denied' | 'pending_info' | 'partial_approval';

export type SubmissionLane = 'fhir_pas' | 'portal' | 'fax' | 'phone';

export type FollowUpType = 'call' | 'portal_check' | 'fax' | 'email';

export interface Patient {
  id: string;
  mrn: string;
  first_name: string;
  last_name: string;
  dob: string;
  primary_payer_id?: string;
  member_id?: string;
  group_number?: string;
}

export interface Payer {
  id: string;
  name: string;
  payer_id: string;
  portal_url?: string;
  phone?: string;
  fax?: string;
  avg_days_to_decision: number;
  epa_enabled: boolean;
}

export interface Facility {
  id: string;
  name: string;
  type: 'hospital' | 'asc' | 'office';
  address?: string;
  phone?: string;
  fax?: string;
}

export interface Surgeon {
  id: string;
  full_name: string;
  specialty?: string;
  npi?: string;
}

export type SubmissionLaneType = 'FHIR_PAS' | 'PORTAL' | 'FAX_PHONE';

export interface AuthorizationRequest {
  id: string;
  case_number: string;
  patient_id: string;
  surgeon_id: string;
  payer_id: string;
  facility_id?: string;
  cpt_codes: string[];
  modifiers?: string[];
  icd10_codes: string[];
  diagnosis_summary: string;
  procedure_description: string;
  scheduled_date?: string;
  urgency: Urgency;
  status: AuthorizationStatus;
  
  // Submission lane tracking
  active_submission_lane?: SubmissionLaneType;
  submission_reference_id?: string;
  submission_proof_path?: string;
  last_submission_at?: string;
  
  // Lifecycle timestamps
  surgeon_approved_at?: string;
  packet_generated_at?: string;
  submitted_to_payer_at?: string;
  more_info_requested_at?: string;
  appeal_submitted_at?: string;
  closed_at?: string;
  
  // Submission details
  submission_lane?: SubmissionLane;
  payer_reference_number?: string;
  payer_response?: PayerResponseType;
  payer_response_date?: string;
  more_info_details?: string;
  
  // Authorization result
  auth_number?: string;
  auth_effective_start?: string;
  auth_effective_end?: string;
  approval_valid_until?: string;
  denial_reason?: string;
  
  // Payer Status Normalization
  payer_raw_status?: string;
  payer_normalized_status?: 'SUBMITTED' | 'IN_REVIEW' | 'NEED_MORE_INFO' | 'APPROVED' | 'DENIED' | 'WITHDRAWN' | 'CLOSED';
  
  // Denial Normalization
  denial_raw_text?: string;
  denial_category?: 'MEDICAL_NECESSITY' | 'DOCUMENTATION_MISSING' | 'CODING_MODIFIER' | 'COVERAGE_LIMITATION' | 'TIMELY_FILING' | 'NON_COVERED_SERVICE' | 'OTHER';
  denial_category_confidence?: 'TEMPLATE' | 'VERIFIED' | 'LEARNED';
  
  // Follow-up tracking
  follow_up_date?: string;
  follow_up_notes?: string;
  sla_deadline?: string;
  
  // Assignment
  link_ops_assigned_to?: string;
  notes?: string;
  
  // === Phase 1 Superset & Compliance Fields ===
  
  // Subset tracking (Phase 1C)
  subset_template_id?: string | null;
  subset_template_version?: number | null;
  selected_subset_cpts?: string[] | null;
  surgeon_attestation?: boolean | null;
  surgeon_attestation_timestamp?: string | null;
  
  // Authorization superset enforcement (Phase 1D)
  authorized_cpts?: string[] | null;
  authorization_scope?: 'SUPERSET' | 'STANDARD' | 'SINGLE' | null;
  
  // Post-op reconciliation (Phase 1E)
  performed_cpts?: string[] | null;
  reconciliation_status?: 'pending' | 'matched' | 'mismatch_resolved' | 'mismatch_unresolved' | null;
  reconciliation_completed_at?: string | null;
  reconciliation_completed_by?: string | null;
  reconciliation_notes?: string | null;
  
  // Post-op documentation (Phase 2B)
  original_op_note_text?: string | null;
  op_note_source_type?: 'paste' | 'upload' | 'ehr' | null;
  op_note_uploaded_at?: string | null;
  op_note_uploaded_by?: string | null;
  op_note_file_path?: string | null;
  
  created_at: string;
  updated_at: string;
  
  // Joined fields
  patient?: Patient;
  payer?: Payer;
  facility?: Facility;
  surgeon?: Surgeon;
}

export interface CaseDocument {
  id: string;
  authorization_id: string;
  document_name: string;
  document_type: string;
  file_path?: string;
  is_required: boolean;
  is_uploaded: boolean;
  uploaded_by?: string;
  uploaded_at?: string;
  created_at: string;
}

export interface CaseFollowUp {
  id: string;
  authorization_id: string;
  scheduled_date: string;
  follow_up_type: FollowUpType;
  completed: boolean;
  completed_at?: string;
  completed_by?: string;
  notes?: string;
  created_at: string;
  created_by?: string;
}

export interface ActivityLogEntry {
  id: string;
  authorization_id: string;
  actor_id: string;
  actor_type: string;
  action: string;
  details?: Record<string, unknown>;
  created_at: string;
}

export interface PayerRule {
  id: string;
  payer_id: string;
  cpt_code: string;
  modifier?: string;
  requires_prior_auth: boolean;
  required_documents?: string[];
  clinical_criteria?: string;
  auth_validity_days: number;
  special_instructions?: string;
  effective_date: string;
  expiration_date?: string;
  payer?: Payer;
}

export interface IntegrationConnection {
  id: string;
  integration_type: 'epic_fhir' | 'epa' | 'clearinghouse';
  name: string;
  status: 'pending' | 'connected' | 'error' | 'disabled';
  config?: Record<string, unknown>;
  last_sync_at?: string;
  error_message?: string;
  created_at: string;
  updated_at: string;
}

// Workflow status labels and colors
export const STATUS_LABELS: Record<AuthorizationStatus, string> = {
  pending_surgeon_approval: 'Awaiting Surgeon',
  surgeon_approved: 'Approved by Surgeon',
  packet_generated: 'Packet Generated',
  submitted_to_payer: 'Submitted to Payer',
  payer_pending: 'In Review',
  more_info_requested: 'More Info Requested',
  payer_approved: 'Approved',
  payer_denied: 'Denied',
  appeal_in_progress: 'Appeal in Progress',
  awaiting_post_op_reconciliation: 'Awaiting Reconciliation',
  resolved: 'Resolved',
  closed: 'Closed',
};

export const STATUS_COLORS: Record<AuthorizationStatus, string> = {
  pending_surgeon_approval: 'bg-amber-500/10 text-amber-500 border-amber-500/30',
  surgeon_approved: 'bg-blue-500/10 text-blue-500 border-blue-500/30',
  packet_generated: 'bg-cyan-500/10 text-cyan-500 border-cyan-500/30',
  submitted_to_payer: 'bg-indigo-500/10 text-indigo-500 border-indigo-500/30',
  payer_pending: 'bg-purple-500/10 text-purple-500 border-purple-500/30',
  more_info_requested: 'bg-orange-500/10 text-orange-500 border-orange-500/30',
  payer_approved: 'bg-primary/10 text-primary border-primary/30',
  payer_denied: 'bg-red-500/10 text-red-500 border-red-500/30',
  appeal_in_progress: 'bg-orange-500/10 text-orange-500 border-orange-500/30',
  awaiting_post_op_reconciliation: 'bg-violet-500/10 text-violet-500 border-violet-500/30',
  resolved: 'bg-slate-500/10 text-slate-500 border-slate-500/30',
  closed: 'bg-slate-500/10 text-slate-400 border-slate-500/30',
};

export const URGENCY_COLORS: Record<Urgency, string> = {
  routine: 'bg-slate-500/10 text-slate-400',
  urgent: 'bg-amber-500/10 text-amber-500',
  emergent: 'bg-red-500/10 text-red-500',
};

export const SUBMISSION_LANE_LABELS: Record<SubmissionLane, string> = {
  fhir_pas: 'FHIR PAS',
  portal: 'Payer Portal',
  fax: 'Fax',
  phone: 'Phone',
};

export const FOLLOW_UP_TYPE_LABELS: Record<FollowUpType, string> = {
  call: 'Phone Call',
  portal_check: 'Portal Check',
  fax: 'Fax',
  email: 'Email',
};

// Workflow progression order
export const WORKFLOW_ORDER: AuthorizationStatus[] = [
  'pending_surgeon_approval',
  'surgeon_approved',
  'packet_generated',
  'submitted_to_payer',
  'payer_pending',
  'more_info_requested',
  'payer_approved',
  'payer_denied',
  'appeal_in_progress',
  'resolved',
  'closed',
];
